import type { Location } from "history";
import type React from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";

export type PreviousLocationContextState = {
    previousLocation: Location | undefined;
    goBack: (fallback?: string) => void;
};

export const PreviousLocationContext = createContext<PreviousLocationContextState>({
    previousLocation: undefined,

    goBack: () => {},
});

export const usePreviousLocation = () => useContext(PreviousLocationContext);

type Props = {
    children: React.JSX.Element | React.JSX.Element[];
};

const CapturePreviousLocation = function LocationsProvider(props: Props) {
    const location = useLocation();
    const history = useHistory();
    const [previousLocation, setPreviousLocation] = useState<Location | undefined>(
        undefined,
    );
    const [currentLocation, setCurrentLocation] = useState<Location | undefined>(
        undefined,
    );

    const locationsContextState = useMemo(() => {
        const goBack = (fallback?: string) => {
            if (previousLocation) {
                history.goBack();
                setPreviousLocation(undefined);
            } else if (fallback) history.push(fallback);
            else history.push("/");
        };
        return {
            previousLocation,
            goBack,
        };
    }, [previousLocation, history]);

    useEffect(() => {
        setPreviousLocation(currentLocation);
        // Ensure key is always a string to match History's Location type
        const locationWithKey = {
            ...location,
            key: location.key ?? "",
        };
        setCurrentLocation(locationWithKey);
    }, [location]);

    const { children } = props;
    return (
        <PreviousLocationContext.Provider value={locationsContextState}>
            {children}
        </PreviousLocationContext.Provider>
    );
};

export default CapturePreviousLocation;
